import { useState, useEffect,useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Dropdown } from "@/components/ui/dropdown";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/tooltip";
import { DateInput } from "@/components/ui/dateInput";
import { TimeInput } from "@/components/ui/timeInput";
import { AddressInput } from "@/components/ui/addressInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ArrowNav from "@/components/ArrowNav";
import { StationResultProps, PickupReturnProps } from "@/lib/type";
import { useNavigate, useSearchParams, } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  cn,
  getHour,
  getMinute,
  getTwoDigitDay,
  getTwoDigitMonth,
} from "@/lib/utils";
import { ageList, countryList } from "@/lib/constant";
import { operationHours } from "@/lib/constant";
import { useRegion } from "@/context/RegionContext";

interface HomeFilterBarProps {
  className?: string;
  filterData?: PickupReturnProps;
  onChange?: any;
}

const HomeFilterBar: React.FC<HomeFilterBarProps> = ({
  className,
  filterData,
  onChange,
}) => {
  const navigate = useNavigate();
   const [searchParams,setSearchParams] = useSearchParams();
  const { region } = useRegion();

  const [isError, setIsError] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [data, setData] = useState<PickupReturnProps>(
    filterData ?? {
      vehicleType: "car",
      pickUpDate: undefined,
      pickUpTime: undefined,
      pickUpLocation: undefined,
      returnDate: undefined,
      returnTime: undefined,
      returnLocation: undefined,
      isSameReturn: true,
      haveNegoRate: false,
      driverAge: "26+",
      country: region.code,
    }
  );
  const [isMobileView, setIsMobileView] = useState(false);
  const [isIntermediateView, setIsIntermediateView] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobileView(width <= 768);
      setIsIntermediateView(width > 768 && width < 1200);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (filterData != undefined) {
      setData(filterData);
    }
  }, [filterData]);
// Function to build search params
const updateSearchParams = (data: any) => {
  const params = new URLSearchParams();

  params.set("vehicleType", data.vehicleType);
  params.set("pickupLocation", data.pickUpLocation.rw_station_id);
  params.set("dropoffLocation", data.returnLocation.rw_station_id);
  params.set("pickupYear", data.pickUpDate.getFullYear().toString());
  params.set("pickupMonth", getTwoDigitMonth(data.pickUpDate));
  params.set("pickupDay", getTwoDigitDay(data.pickUpDate));
  params.set("dropoffYear", data.returnDate.getFullYear().toString());
  params.set("dropoffMonth", getTwoDigitMonth(data.returnDate));
  params.set("dropoffDay", getTwoDigitDay(data.returnDate));
  params.set("pickupHour", getHour(data.pickUpTime));
  params.set("pickupMinute", getMinute(data.pickUpTime));
  params.set("dropoffHour", getHour(data.returnTime));
  params.set("dropoffMinute", getMinute(data.returnTime));
  params.set("driverAge", data.driverAge);
  params.set("countryOfResidence", data.country);

  if (data.haveNegoRate) {
    params.set("promoCode", data.negoRate);
  }

  return params;
};
//var dayOfWeek = yourDateObject.getDay();
// var isWeekend = (dayOfWeek === 6) || (dayOfWeek  === 0)

const pickUpOperationHours = useMemo(() => {

}, [data.pickUpDate,data.pickUpLocation,data.pickUpTime]);
const returnOperationHours = useMemo(() => {
  
}, [data.returnDate,data.returnLocation,data.returnTime]);

// Function to track search event
const trackSearchEvent = (data: PickupReturnProps) => {
  try {
    const isStaging = import.meta.env.VITE_BUILD_ENV === 'staging';
    const isDebugMode = import.meta.env.VITE_GA_DEBUG === 'true';
    
    // Create event parameters following GA4 structure
    const eventParams = {
      // Standard parameters
      currency: region.currencyCode,
      value: 0, // Will be populated when actual vehicle is selected
      // Items array for enhanced measurement
      items: [{
        item_id: `${data.pickUpLocation?.rw_station_id}_${data.returnLocation?.rw_station_id}`,
        item_name: `${data.pickUpLocation?.name} to ${data.returnLocation?.name}`,
        item_category: data.vehicleType,
        item_variant: data.isSameReturn ? 'same_location' : 'different_location'
      }],
      // Custom parameters need to be prefixed with 'custom_'
      custom_search_term: `${data.pickUpLocation?.name} to ${data.returnLocation?.name}`,
      custom_pickup_station: data.pickUpLocation?.name || '',
      custom_pickup_station_id: data.pickUpLocation?.rw_station_id || '',
      custom_return_station: data.returnLocation?.name || '',
      custom_return_station_id: data.returnLocation?.rw_station_id || '',
      custom_pickup_date: data.pickUpDate?.toISOString().split('T')[0] || '',
      custom_return_date: data.returnDate?.toISOString().split('T')[0] || '',
      custom_driver_age_group: data.driverAge || '',
      custom_residence_country: data.country || '',
      custom_same_return_location: data.isSameReturn
    };

    // Debug logs
    if (isDebugMode) {
      console.group('GA4 View Item Event');
      console.log('Event Name:', 'view_item');
      console.log('Event Parameters:', eventParams);
      console.log('GA4 Initialized:', typeof window.gtag === 'function');
      console.log('Environment:', import.meta.env.VITE_BUILD_ENV);
      console.log('Debug Mode:', isDebugMode);
      console.log('Purchase Funnel Step:', 2);
    }
    
    // Send event to GA4
    if (typeof window.gtag === 'function') {
      // Configure debug mode for this event
      if (isDebugMode) {
        window.gtag('config', 'G-8MP71FT74Q', {
          debug_mode: true
        });
      }
      
      window.gtag('event', 'view_item', {  // Using view_item for step 2
        ...eventParams,
        debug_mode: isDebugMode
      });
      isDebugMode && console.log('GA4 event sent via gtag with debug mode:', isDebugMode);
    }
    
    ReactGA.event('view_item', {  // Using view_item for step 2
      ...eventParams,
      debug_mode: isDebugMode
    });
    isDebugMode && console.log('GA4 event sent via ReactGA with debug mode:', isDebugMode);
    
    isDebugMode && console.groupEnd();
  } catch (error) {
    console.error('Error sending GA4 event:', error);
    
    // Fallback to direct gtag if available
    try {
      if (typeof window.gtag === 'function') {
        const isDebugMode = import.meta.env.VITE_GA_DEBUG === 'true';
        window.gtag('event', 'view_item', {  // Using view_item for step 2
          custom_search_term: `${data.pickUpLocation?.name} to ${data.returnLocation?.name}`,
          item_category: data.vehicleType,
          custom_pickup_station: data.pickUpLocation?.name,
          custom_return_station: data.returnLocation?.name,
          debug_mode: isDebugMode
        });
        isDebugMode && console.log('Fallback GA4 event sent with debug mode:', isDebugMode);
      } else {
        console.error('GA4 tracking unavailable - gtag not found');
      }
    } catch (gtagError) {
      console.error('Error sending fallback GA4 event:', gtagError);
    }
  }
};

  return (
    <div className={cn("bg-white mt-2 rounded-md p-7", className)}>
      <div className="font-bold">What type of vehicle?</div>
      <div className="flex flex-row md:space-x-0 space-x-2 mt-2">
        <span
          className={`border-gray-300 px-4 py-3 rounded-md  flex flex-row items-center cursor-pointer ${data.vehicleType == "car" ? "bg-green-600 text-white font-medium" : "border-[1px] md:border-r-0"}`}
          onClick={() => setData({ ...data, vehicleType: "car" })}
        >
          <img src="/home/car.png" className="max-h-5 mr-2" /> Car
        </span>
        {/* <span
          className={`border-gray-300 px-4 py-3 rounded-md md:rounded-l-none flex flex-row items-center cursor-pointer ${data.vehicleType == "vansTruck" ? "bg-green-600 text-white font-medium" : "border-[1px] md:border-l-0"}`}
          onClick={() => setData({ ...data, vehicleType: "vansTruck" })}
        >
          <img src="home/vans-truck.png" className="max-h-5 mr-2" /> Vans &
          Truck
        </span> */}
      </div>
      <div className="flex flex-col md:grid md:grid-cols-4 gap-4 md:gap-3 md:items-center mt-2">
        {/* Pickup Section */}
        <div className={`${isIntermediateView ? "md:col-span-4" : "md:col-span-2"}`}>
          <div className="flex flex-row justify-between items-center">
            <div className="font-bold">Pick up & Return location</div>
            <div className="flex flex-row md:block hidden">
              <Checkbox
                id="sameReturn"
                value={data.isSameReturn}
                onClick={() =>
                  {
                    if(!data.isSameReturn){
                      setData({ ...data, isSameReturn: !data.isSameReturn , returnLocation: data.pickUpLocation,returnTime:undefined,returnDate:undefined})                    }
                    else{
                      setData({ ...data, isSameReturn: !data.isSameReturn , returnLocation: undefined,returnTime:undefined,returnDate:undefined})
                    }
                  }}
                label="Same return location"
                className="ml-2"
              />
            </div>
          </div>
          {data.isSameReturn ? (
            <AddressInput
              key={"pickReturnLocation"}
              value={data.pickUpLocation}
              prefix="/icon/location.svg"
              placeholder="Pick up & Return location"
              id="pickReturnLocation"
              className="mt-2"
              onSelect={(location: StationResultProps) => {
                setData({
                  ...data,
                  pickUpLocation: location,
                  returnLocation: location,
                });
                setFocusIndex(2);
              }}
              showError={
                isError &&
                data.pickUpLocation == undefined &&
                data.returnLocation == undefined
              }
            />
          ) : (
            <div className="flex flex-col md:grid md:grid-cols-4 gap-4 md:gap-3 md:items-center mt-2">
              <div className={isIntermediateView ? "md:col-span-4" : "md:col-span-2"}>
                <AddressInput
                  key={"pickLocation"}
                  value={data.pickUpLocation}
                  prefix="/icon/location.svg"
                  placeholder="Pick up location"
                  id="pickLocation"
                  onSelect={(location: StationResultProps) =>
                    setData({ ...data, pickUpLocation: location })
                  }
                  showError={isError && data.pickUpLocation == undefined}
                />
              </div>
              <div className={isIntermediateView ? "md:col-span-4 mt-3" : "md:col-span-2"}>
                <AddressInput
                  key={"returnLocation"}
                  value={data.returnLocation}
                  prefix="/icon/location.svg"
                  placeholder="Return location"
                  id="returnLocation"
                  onSelect={(location: StationResultProps) =>
                    setData({ ...data, returnLocation: location })
                  }
                  showError={isError && data.returnLocation == undefined}
                />
              </div>
            </div>
          )}
        </div>
        <div className="md:hidden block">
          <Checkbox
            id="sameReturn"
            value={data.isSameReturn}
            onClick={() =>
              setData({ ...data, isSameReturn: !data.isSameReturn , returnLocation: undefined,returnTime:undefined,returnDate:undefined})
            }
            label="Same return location"
            checkboxClassName="w-6 h-6"
          />
        </div>

        {/* Date and Time Section - Restructured for intermediate layout */}
        {isIntermediateView ? (
          <>
            <div className={`md:col-span-2 ${
              isMobileView &&
              (data.pickUpLocation == undefined ||
                data.returnLocation == undefined)
                ? `hidden`
                : ``
            }`}>
              <div className="font-bold">Pick up date & time</div>
              <div className="flex flex-row mt-2 gap-1">
                <DateInput
                  id="pickDate"
                  prefix="/icon/calendar.svg"
                  placeholder="Pick up date"
                  className={`${isMobileView ? 'flex-[2]' : 'flex-[3]'} flex-shrink-0`}
                  date={data.pickUpDate}
                  setDate={(selectedDate) => {
                    if (selectedDate instanceof Date) {
                      // When a pick-up date is selected, update both pickUpDate and set it as the "from" in dateRange
                      setData({ 
                        ...data, 
                        pickUpDate: selectedDate,
                        // Clear return date if it's before the new pick-up date
                        returnDate: data.returnDate && data.returnDate < selectedDate ? undefined : data.returnDate
                      });
                    } else if (selectedDate && 'from' in selectedDate && selectedDate.from) {
                      setData({ 
                        ...data, 
                        pickUpDate: selectedDate.from,
                        // If "to" is also selected, update the return date
                        returnDate: selectedDate.to
                      });
                    }
                  }}
                  popupClassName=""
                  showError={isError && data.pickUpDate == undefined}
                  isFocus={focusIndex == 2}
                  mode="range"
                  dateRange={{ from: data?.pickUpDate, to: data?.returnDate }}
                />
                <TimeInput
                  id="pickTime"
                  placeholder="Time"
                  className={`${isMobileView ? 'flex-[1]' : 'flex-[1]'} text-center`}
                  popupClassName=""
                  time={data.pickUpTime}
                  setTime={(time) => {
                    setData({ ...data, pickUpTime: time });
                    setFocusIndex(3);
                  }}
                  showError={isError && data.pickUpTime == undefined}
                  isFocus={focusIndex == 2 && data.pickUpDate != undefined}
                  stationLocation={data.pickUpLocation}
                  selectedDate={data.pickUpDate}
                />
              </div>
              <div
                className={`text-xs ${isError && (data.pickUpDate == undefined || data.pickUpTime == undefined) ? "text-red-500" : "hidden"} font-medium flex flex-row items-center space-x-1 mt-1`}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Pick up date & time cannot be empty</span>
              </div>
            </div>
            
            <div className={`md:col-span-2 ${
              isMobileView 
              &&
              (data.pickUpDate == undefined &&
              data.pickUpTime == undefined)
                ? `hidden`
                : ``
            }`}>
              <div className="font-bold">Return date & time</div>
              <div className="flex flex-row mt-2 gap-1">
                <DateInput
                  id="returnDate"
                  prefix="/icon/calendar.svg"
                  placeholder="Return date"
                  className={`${isMobileView ? 'flex-[2]' : 'flex-[3]'} flex-shrink-0`}
                  date={data.returnDate}
                  minDate={
                    data.pickUpDate?.getTime() == undefined
                      ? undefined
                      : new Date(data.pickUpDate?.getTime() + 60 * 60 * 24 * 1000)
                  }
                  setDate={(selectedDate) => {
                    if (selectedDate instanceof Date) {
                      setData({ ...data, returnDate: selectedDate });
                    } else if (selectedDate && 'to' in selectedDate && selectedDate.to) {
                      setData({ ...data, returnDate: selectedDate.to });
                    }
                  }}
                  popupClassName=""
                  showError={isError && data.returnDate == undefined}
                  isFocus={focusIndex == 3}
                  mode="range"
                  dateRange={{ 
                    from: data?.pickUpDate, 
                    to: data?.returnDate 
                  }}
                />
                <TimeInput
                  id="returnTime"
                  placeholder="Time"
                  className={`${isMobileView ? 'flex-[1]' : 'flex-[1]'} text-center`}
                  popupClassName=""
                  time={data.returnTime}
                  setTime={(time) => setData({ ...data, returnTime: time })}
                  showError={isError && data.returnTime == undefined}
                  isFocus={focusIndex == 3 && data.returnDate != undefined}
                  stationLocation={data.returnLocation}
                  selectedDate={data.returnDate}
                />
              </div>
              <div
                className={`text-xs ${isError && (data.pickUpDate == undefined || data.pickUpTime == undefined) ? "text-red-500" : "hidden"} font-medium flex flex-row items-center space-x-1 mt-1`}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Return date & time cannot be empty</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className={
                isMobileView &&
                (data.pickUpLocation == undefined ||
                  data.returnLocation == undefined)
                  ? `hidden`
                  : ``
              }
            >
              <div className="font-bold">Pick up date & time</div>
              <div className="flex flex-row mt-2 gap-1">
                <DateInput
                  id="pickDate"
                  prefix="/icon/calendar.svg"
                  placeholder="Pick up date"
                  className={`${isMobileView ? 'flex-[2]' : 'flex-[3]'} flex-shrink-0`}
                  date={data.pickUpDate}
                  setDate={(selectedDate) => {
                    if (selectedDate instanceof Date) {
                      // When a pick-up date is selected, update both pickUpDate and set it as the "from" in dateRange
                      setData({ 
                        ...data, 
                        pickUpDate: selectedDate,
                        // Clear return date if it's before the new pick-up date
                        returnDate: data.returnDate && data.returnDate < selectedDate ? undefined : data.returnDate
                      });
                    } else if (selectedDate && 'from' in selectedDate && selectedDate.from) {
                      setData({ 
                        ...data, 
                        pickUpDate: selectedDate.from,
                        // If "to" is also selected, update the return date
                        returnDate: selectedDate.to
                      });
                    }
                  }}
                  popupClassName=""
                  showError={isError && data.pickUpDate == undefined}
                  isFocus={focusIndex == 2}
                  mode="range"
                  dateRange={{ from: data?.pickUpDate, to: data?.returnDate }}
                />
                <TimeInput
                  id="pickTime"
                  placeholder="Time"
                  className={`${isMobileView ? 'flex-[1]' : 'flex-[1]'} text-center`}
                  popupClassName=""
                  time={data.pickUpTime}
                  setTime={(time) => {
                    setData({ ...data, pickUpTime: time });
                    setFocusIndex(3);
                  }}
                  showError={isError && data.pickUpTime == undefined}
                  isFocus={focusIndex == 2 && data.pickUpDate != undefined}
                  stationLocation={data.pickUpLocation}
                  selectedDate={data.pickUpDate}
                />
              </div>
              <div
                  className={`text-xs ${isError && (data.pickUpDate == undefined || data.pickUpTime == undefined) ? "text-red-500" : "hidden"} font-medium flex flex-row items-center space-x-1 mt-1`}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Pick up date & time cannot be empty</span>
              </div>
            </div>
            
            <div
              className={
                isMobileView 
                &&
                (data.pickUpDate == undefined &&
                data.pickUpTime == undefined)
                  ? `hidden`
                  : ``
              }
            >
              <div className="font-bold">Return date & time</div>
              <div className="flex flex-row mt-2 gap-1">
                <DateInput
                  id="returnDate"
                  prefix="/icon/calendar.svg"
                  placeholder="Return date"
                  className={`${isMobileView ? 'flex-[2]' : 'flex-[3]'} flex-shrink-0`}
                  date={data.returnDate}
                  minDate={
                    data.pickUpDate?.getTime() == undefined
                      ? undefined
                      : new Date(data.pickUpDate?.getTime() + 60 * 60 * 24 * 1000)
                  }
                  setDate={(selectedDate) => {
                    if (selectedDate instanceof Date) {
                      setData({ ...data, returnDate: selectedDate });
                    } else if (selectedDate && 'to' in selectedDate && selectedDate.to) {
                      setData({ ...data, returnDate: selectedDate.to });
                    }
                  }}
                  popupClassName=""
                  showError={isError && data.returnDate == undefined}
                  isFocus={focusIndex == 3}
                  mode="range"
                  dateRange={{ 
                    from: data?.pickUpDate, 
                    to: data?.returnDate 
                  }}
                />
                <TimeInput
                  id="returnTime"
                  placeholder="Time"
                  className={`${isMobileView ? 'flex-[1]' : 'flex-[1]'} text-center`}
                  popupClassName=""
                  time={data.returnTime}
                  setTime={(time) => setData({ ...data, returnTime: time })}
                  showError={isError && data.returnTime == undefined}
                  isFocus={focusIndex == 3 && data.returnDate != undefined}
                  stationLocation={data.returnLocation}
                  selectedDate={data.returnDate}
                />
              </div>
              <div
                  className={`text-xs ${isError && (data.pickUpDate == undefined || data.pickUpTime == undefined) ? "text-red-500" : "hidden"} font-medium flex flex-row items-center space-x-1 mt-1`}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Return date & time cannot be empty</span>
              </div>
            </div>
          </>
        )}
      </div>
     
        <div className="flex flex-col md:grid md:grid-cols-4 gap-4 md:gap-3 md:items-center mt-2">
      
        <div className="flex flex-row md:col-span-2">
          <div className="w-2/5 md:w-full flex flex-row">
            I am <Dropdown id="age" optionList={ageList} defaultValue="26+" onChange={(e)=>{setData({ ...data, driverAge: e })}}/>
          </div>
          <div className="w-3/5 md:w-full flex flex-row">
            I live in <Dropdown id="country" className="w-5" optionList={countryList} defaultValue={region.countryName} onChange={(e)=>{setData({ ...data, country: e })}}/>
          </div>
        </div>
        <div className="md:justify-self-end flex relative">
          {/* Negotiated rate feature will be implemented later */}
        </div>
        <Button
        className="py-6"
        onClick={() => {
          // for reservation page
          if (onChange != undefined) {
            console.log(data);
            onChange(data);
            if (
              data.pickUpLocation == undefined ||
              data.returnLocation == undefined ||
              data.pickUpDate == undefined ||
              data.pickUpTime == undefined ||
              data.returnDate == undefined ||
              data.returnTime == undefined
            ) {
              setIsError(true);
            } else {
              setIsError(false);
              // Track search event
              trackSearchEvent(data);
              const newParams = updateSearchParams(data);
              setSearchParams(newParams);
              navigate(
                `/reservation?vehicleType=${data.vehicleType}&pickupLocation=${data.pickUpLocation.rw_station_id}&dropoffLocation=${data.returnLocation.rw_station_id}&pickupYear=${data.pickUpDate.getFullYear()}&pickupMonth=${getTwoDigitMonth(data.pickUpDate)}&pickupDay=${getTwoDigitDay(data.pickUpDate)}&dropoffYear=${data.returnDate.getFullYear()}&dropoffMonth=${getTwoDigitMonth(data.returnDate)}&dropoffDay=${getTwoDigitDay(data.returnDate)}&pickupHour=${getHour(data.pickUpTime)}&pickupMinute=${getMinute(data.pickUpTime)}&dropoffHour=${getHour(data.returnTime)}&dropoffMinute=${getMinute(data.returnTime)}&driverAge=${data.driverAge}&countryOfResidence=${data.country}${data.haveNegoRate ? `&promoCode=${data.negoRate}` : ""}`
                , { replace: true }
              );
            }
          } else {
            if (
              data.pickUpLocation == undefined ||
              data.returnLocation == undefined ||
              data.pickUpDate == undefined ||
              data.pickUpTime == undefined ||
              data.returnDate == undefined ||
              data.returnTime == undefined
            ) {
              setIsError(true);
            } else {
              setIsError(false);
              // Track search event
              trackSearchEvent(data);
              navigate(
                `/reservation?vehicleType=${data.vehicleType}&pickupLocation=${data.pickUpLocation.rw_station_id}&dropoffLocation=${data.returnLocation.rw_station_id}&pickupYear=${data.pickUpDate.getFullYear()}&pickupMonth=${getTwoDigitMonth(data.pickUpDate)}&pickupDay=${getTwoDigitDay(data.pickUpDate)}&dropoffYear=${data.returnDate.getFullYear()}&dropoffMonth=${getTwoDigitMonth(data.returnDate)}&dropoffDay=${getTwoDigitDay(data.returnDate)}&pickupHour=${getHour(data.pickUpTime)}&pickupMinute=${getMinute(data.pickUpTime)}&dropoffHour=${getHour(data.returnTime)}&dropoffMinute=${getMinute(data.returnTime)}&driverAge=${data.driverAge}&countryOfResidence=${data.country}${data.haveNegoRate ? `&promoCode=${data.negoRate}` : ""}`
                , { replace: true }
              );
            }
          }
        }}
      >
        Search
      </Button>
      </div>
      
    </div>
  );
};

export default HomeFilterBar;
